import { Button } from "@akuna/ui";
// src/pages/erp/fiscal/NfseTab.tsx
import { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@akuna/ui";
import { Label } from "@/components/ui/label";
import { Switch } from "@akuna/ui";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { NfseSchema, type NfseFormValues } from "./fiscal-settings.schemas";
import type { FiscalTabProps } from "./fiscal-settings.types";
import { getNfseMunicipalProfile, isNfseFieldSupported } from "@/lib/fiscal/nfse/profiles";
import { FocusServiceListAutocomplete } from "@/components/erp/FocusServiceListAutocomplete";
import { FocusCnaeAutocomplete } from "@/components/erp/FocusCnaeAutocomplete";

const NFSE_REGIME_ESPECIAL_VALUES = new Set(["1", "2", "3", "4", "5", "6"]);

function normalizeNfseRegimeEspecial(value: string | null | undefined): NfseFormValues["default_nfse_regime_especial_tributacao"] {
  const normalized = String(value ?? "").trim();
  return NFSE_REGIME_ESPECIAL_VALUES.has(normalized)
    ? normalized as NfseFormValues["default_nfse_regime_especial_tributacao"]
    : "";
}

export function NfseTab({ environment, focusData, focusEmpresaId, localDefaults, clinicId, customerId }: FiscalTabProps) {
  const { t } = useTranslation("erp");

  const profile = getNfseMunicipalProfile(localDefaults?.codigo_municipio_emitente ?? undefined);
  const showServiceCode = isNfseFieldSupported(profile, "nfse_service_code");
  const showCnae = isNfseFieldSupported(profile, "nfse_cnae");

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<NfseFormValues>({
    resolver: zodResolver(NfseSchema),
    defaultValues: {
      habilita_nfse: false,
      habilita_nfsen_homologacao: false,
      habilita_nfsen_producao: false,
      default_nfse_service_list_item: "",
      default_nfse_service_code: "",
      default_nfse_cnae: "",
      default_nfse_iss_rate: 0,
      default_nfse_natureza_operacao: "1",
      default_nfse_regime_especial_tributacao: "",
      default_nfse_optante_simples_nacional: false,
      default_nfse_iss_retido: false,
      default_nfse_series: "",
      default_nfse_ibs_cbs_situacao_tributaria: "",
      default_nfse_ibs_cbs_classificacao_tributaria: "",
      default_nfse_ibs_uf_aliquota: null,
      default_nfse_ibs_mun_aliquota: null,
      default_nfse_cbs_aliquota: null,
    },
  });

  useEffect(() => {
    reset({
      habilita_nfse: focusData?.habilita_nfse ?? false,
      habilita_nfsen_homologacao: focusData?.habilita_nfsen_homologacao ?? false,
      habilita_nfsen_producao: focusData?.habilita_nfsen_producao ?? false,
      default_nfse_service_list_item: localDefaults?.default_nfse_service_list_item ?? "",
      default_nfse_service_code: localDefaults?.default_nfse_service_code ?? "",
      default_nfse_cnae: localDefaults?.default_nfse_cnae ?? "",
      default_nfse_iss_rate: localDefaults?.default_nfse_iss_rate ?? 0,
      default_nfse_natureza_operacao: (localDefaults?.default_nfse_natureza_operacao as NfseFormValues["default_nfse_natureza_operacao"]) ?? "1",
      default_nfse_regime_especial_tributacao: normalizeNfseRegimeEspecial(localDefaults?.default_nfse_regime_especial_tributacao),
      default_nfse_optante_simples_nacional: localDefaults?.default_nfse_optante_simples_nacional ?? false,
      default_nfse_iss_retido: localDefaults?.default_nfse_iss_retido ?? false,
      default_nfse_series: localDefaults?.default_nfse_series ?? "",
      default_nfse_ibs_cbs_situacao_tributaria: localDefaults?.default_nfse_ibs_cbs_situacao_tributaria ?? "",
      default_nfse_ibs_cbs_classificacao_tributaria: localDefaults?.default_nfse_ibs_cbs_classificacao_tributaria ?? "",
      default_nfse_ibs_uf_aliquota: localDefaults?.default_nfse_ibs_uf_aliquota ?? null,
      default_nfse_ibs_mun_aliquota: localDefaults?.default_nfse_ibs_mun_aliquota ?? null,
      default_nfse_cbs_aliquota: localDefaults?.default_nfse_cbs_aliquota ?? null,
    });
  }, [focusData, localDefaults, reset]);

  const onSubmit = async (values: NfseFormValues) => {
    // 1. Save flags to FocusNFe
    const focusPayload: Record<string, unknown> = {};
    if (environment === "homologacao") {
      focusPayload.habilita_nfse = values.habilita_nfse;
      focusPayload.habilita_nfsen_homologacao = values.habilita_nfsen_homologacao;
    } else {
      focusPayload.habilita_nfsen_producao = values.habilita_nfsen_producao;
    }

    const { error: focusError } = await supabase.functions.invoke("fiscal-manage-empresa", {
      body: { method: "PUT", focus_empresa_id: focusEmpresaId, payload: focusPayload },
    });
    if (focusError) {
      toast.error(t("fiscalSettings.nfse.saveError"));
      return;
    }

    // 2. Upsert local defaults
    const localPayload = {
      clinic_id: clinicId,
      customer_id: customerId,
      default_nfse_service_list_item: values.default_nfse_service_list_item || null,
      default_nfse_service_code: values.default_nfse_service_code || null,
      default_nfse_cnae: values.default_nfse_cnae || null,
      default_nfse_iss_rate: values.default_nfse_iss_rate,
      default_nfse_natureza_operacao: values.default_nfse_natureza_operacao,
      default_nfse_regime_especial_tributacao: values.default_nfse_regime_especial_tributacao || null,
      default_nfse_optante_simples_nacional: values.default_nfse_optante_simples_nacional,
      default_nfse_iss_retido: values.default_nfse_iss_retido,
      default_nfse_series: values.default_nfse_series || null,
      default_nfse_ibs_cbs_situacao_tributaria: values.default_nfse_ibs_cbs_situacao_tributaria || null,
      default_nfse_ibs_cbs_classificacao_tributaria: values.default_nfse_ibs_cbs_classificacao_tributaria || null,
      default_nfse_ibs_uf_aliquota: values.default_nfse_ibs_uf_aliquota ?? null,
      default_nfse_ibs_mun_aliquota: values.default_nfse_ibs_mun_aliquota ?? null,
      default_nfse_cbs_aliquota: values.default_nfse_cbs_aliquota ?? null,
    };

    const { error: dbError } = await supabase
      .from("clinic_fiscal_settings")
      .upsert(localPayload, { onConflict: "clinic_id" });

    if (dbError) {
      toast.error(t("fiscalSettings.nfse.saveError"));
      return;
    }

    toast.success(t("fiscalSettings.nfse.saveSuccess"));
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Flags de habilitação — ambiente-dependentes */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
          {t("fiscalSettings.nfse.enableSection")}
        </h3>

        {environment === "homologacao" && (
          <>
            <div className="flex items-center justify-between">
              <Label>{t("fiscalSettings.nfse.fields.habilitaNfse")}</Label>
              <Controller
                control={control}
                name="habilita_nfse"
                render={({ field }) => (
                  <Switch checked={field.value ?? false} onCheckedChange={field.onChange} disabled={isSubmitting} />
                )}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>{t("fiscalSettings.nfse.fields.habilitaNfsenHomologacao")}</Label>
              <Controller
                control={control}
                name="habilita_nfsen_homologacao"
                render={({ field }) => (
                  <Switch checked={field.value ?? false} onCheckedChange={field.onChange} disabled={isSubmitting} />
                )}
              />
            </div>
          </>
        )}

        {environment === "producao" && (
          <div className="flex items-center justify-between">
            <Label>{t("fiscalSettings.nfse.fields.habilitaNfsenProducao")}</Label>
            <Controller
              control={control}
              name="habilita_nfsen_producao"
              render={({ field }) => (
                <Switch checked={field.value ?? false} onCheckedChange={field.onChange} disabled={isSubmitting} />
              )}
            />
          </div>
        )}
      </div>

      {/* Defaults de emissão */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
          {t("fiscalSettings.nfse.defaultsSection")}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{t("fiscalSettings.nfse.fields.serviceListItem")}</Label>
            <Controller
              control={control}
              name="default_nfse_service_list_item"
              render={({ field }) => (
                <FocusServiceListAutocomplete
                  municipalityCode={localDefaults?.codigo_municipio_emitente ?? "3550308"}
                  value={field.value ?? ""}
                  onChange={(value) => field.onChange(value)}
                  placeholder={t("fiscalSettings.nfse.fields.serviceListItem")}
                  disabled={isSubmitting}
                />
              )}
            />
            {errors.default_nfse_service_list_item && (
              <p className="text-sm text-destructive">{errors.default_nfse_service_list_item.message}</p>
            )}
          </div>

          {showServiceCode && (
            <div className="space-y-2">
              <Label>{t("fiscalSettings.labels.defaultNfseServiceCode")}</Label>
              <Input {...register("default_nfse_service_code")} disabled={isSubmitting} />
            </div>
          )}

          {showCnae && (
            <div className="space-y-2">
              <Label>{t("fiscalSettings.labels.defaultNfseCnae")}</Label>
              <Controller
                control={control}
                name="default_nfse_cnae"
                render={({ field }) => (
                  <FocusCnaeAutocomplete
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    placeholder={t("fiscalSettings.labels.defaultNfseCnae")}
                    disabled={isSubmitting}
                  />
                )}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label>{t("fiscalSettings.nfse.fields.issRate")}</Label>
            <Input
              type="number"
              step="0.01"
              {...register("default_nfse_iss_rate", { valueAsNumber: true })}
              disabled={isSubmitting}
            />
            {errors.default_nfse_iss_rate && (
              <p className="text-sm text-destructive">{errors.default_nfse_iss_rate.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>{t("fiscalSettings.nfse.fields.naturezaOperacao")}</Label>
            <Controller
              control={control}
              name="default_nfse_natureza_operacao"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange} disabled={isSubmitting}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">{t("fiscalSettings.nfse.naturezaOperacao.1")}</SelectItem>
                    <SelectItem value="2">{t("fiscalSettings.nfse.naturezaOperacao.2")}</SelectItem>
                    <SelectItem value="3">{t("fiscalSettings.nfse.naturezaOperacao.3")}</SelectItem>
                    <SelectItem value="4">{t("fiscalSettings.nfse.naturezaOperacao.4")}</SelectItem>
                    <SelectItem value="5">{t("fiscalSettings.nfse.naturezaOperacao.5")}</SelectItem>
                    <SelectItem value="6">{t("fiscalSettings.nfse.naturezaOperacao.6")}</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          <div className="space-y-2">
            <Label>{t("fiscalSettings.nfse.fields.regimeEspecial")}</Label>
            <Controller
              control={control}
              name="default_nfse_regime_especial_tributacao"
              render={({ field }) => (
                <Select
                  value={field.value || "none"}
                  onValueChange={(value) => field.onChange(value === "none" ? "" : value)}
                  disabled={isSubmitting}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t("fiscalSettings.nfse.regimeEspecial.none")}</SelectItem>
                    <SelectItem value="1">{t("fiscalSettings.nfse.regimeEspecial.1")}</SelectItem>
                    <SelectItem value="2">{t("fiscalSettings.nfse.regimeEspecial.2")}</SelectItem>
                    <SelectItem value="3">{t("fiscalSettings.nfse.regimeEspecial.3")}</SelectItem>
                    <SelectItem value="4">{t("fiscalSettings.nfse.regimeEspecial.4")}</SelectItem>
                    <SelectItem value="5">{t("fiscalSettings.nfse.regimeEspecial.5")}</SelectItem>
                    <SelectItem value="6">{t("fiscalSettings.nfse.regimeEspecial.6")}</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          <div className="space-y-2">
            <Label>{t("fiscalSettings.nfse.fields.series")}</Label>
            <Input {...register("default_nfse_series")} disabled={isSubmitting} />
          </div>

          <div className="flex items-center justify-between">
            <Label>{t("fiscalSettings.nfse.fields.optanteSimples")}</Label>
            <Controller
              control={control}
              name="default_nfse_optante_simples_nacional"
              render={({ field }) => (
                <Switch checked={field.value} onCheckedChange={field.onChange} disabled={isSubmitting} />
              )}
            />
          </div>

          <div className="flex items-center justify-between">
            <Label>{t("fiscalSettings.nfse.fields.issRetido")}</Label>
            <Controller
              control={control}
              name="default_nfse_iss_retido"
              render={({ field }) => (
                <Switch checked={field.value} onCheckedChange={field.onChange} disabled={isSubmitting} />
              )}
            />
          </div>
        </div>

        {/* IBS/CBS — campos opcionais */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2 md:col-span-2">
            <Label>{t("fiscalSettings.nfse.fields.ibsCbsSituacao")}</Label>
            <Input
              {...register("default_nfse_ibs_cbs_situacao_tributaria")}
              disabled={isSubmitting}
              placeholder={t("fiscalSettings.nfse.optionalByMunicipality")}
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label>{t("fiscalSettings.nfse.fields.ibsCbsClassificacao")}</Label>
            <Input
              {...register("default_nfse_ibs_cbs_classificacao_tributaria")}
              disabled={isSubmitting}
              placeholder={t("fiscalSettings.nfse.optionalByMunicipality")}
            />
          </div>
          <div className="space-y-2">
            <Label>{t("fiscalSettings.nfse.fields.ibsUf")}</Label>
            <Input
              type="number"
              step="0.01"
              {...register("default_nfse_ibs_uf_aliquota", { valueAsNumber: true })}
              disabled={isSubmitting}
            />
          </div>
          <div className="space-y-2">
            <Label>{t("fiscalSettings.nfse.fields.ibsMun")}</Label>
            <Input
              type="number"
              step="0.01"
              {...register("default_nfse_ibs_mun_aliquota", { valueAsNumber: true })}
              disabled={isSubmitting}
            />
          </div>
          <div className="space-y-2">
            <Label>{t("fiscalSettings.nfse.fields.cbs")}</Label>
            <Input
              type="number"
              step="0.01"
              {...register("default_nfse_cbs_aliquota", { valueAsNumber: true })}
              disabled={isSubmitting}
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          {t("fiscalSettings.nfse.save")}
        </Button>
      </div>
    </form>
  );
}
